import {format} from 'date-fns';
import {combine, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {dateModel} from '@entities/date/model';
import {createGridTableModel, initFiltersTable, initSortingsTable} from '@shared/composables/table';
import {InnerFilters} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';
import {
    DictionarySearchResponse,
    ViolationDictionaryItem,
    ViolationFilterNode,
    ViolationSearchResponse,
    ViolationsRequestParams,
} from './types';

const domain = rootDomain.createDomain('Violations');

export const ViolationsGate = createGate<{tin: string}>('Violations');

const fetchViolationsFx = domain.createEffect({handler: api.fetchViolations});
const fetchViolationTypesFx = domain.createEffect({handler: api.fetchViolationTypes});
const fetchViolationStatusesFx = domain.createEffect({handler: api.fetchViolationStatuses});
const fetchNotificationStatusesFx = domain.createEffect({handler: api.fetchNotificationStatuses});

[
    fetchViolationsFx,
    fetchViolationTypesFx,
    fetchViolationStatusesFx,
    fetchNotificationStatusesFx,
].forEach((fx) => fx.failData.watch(showRestError));

const grid = createGridTableModel<{tin: string}>({
    domain,
    Gate: ViolationsGate,
});

export const {
    filters$,
    filtersChanged,
    defaultFilters,
    filtersReset,
    sortings$,
    sortingsChanged,
    defaultSortings,
    sortingsReset,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    tableStateChanged,
    stateReset,
} = grid;

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

pageNumber$.reset(pageSizeChanged, stateReset);
pageSize$.reset(stateReset);

const data$ = domain
    .createStore<ViolationSearchResponse | null>(null)
    .on(fetchViolationsFx.doneData, (_, data) => data)
    .reset(ViolationsGate.close);

const violationTypes$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(ViolationsGate.close);

const violationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(fetchViolationStatusesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(ViolationsGate.close);

const notificationStatuses$ = domain
    .createStore<ViolationDictionaryItem[]>([])
    .on(
        fetchNotificationStatusesFx.doneData,
        (_, data: DictionarySearchResponse) => data.data ?? [],
    )
    .reset(ViolationsGate.close);

const getSelectedIds = (value: InnerFilters[string]) =>
    ((value as Array<SelectOption<string>> | undefined) ?? [])
        .map((item) => item.value)
        .filter((item): item is string => Boolean(item));

const getSelectedCode = (value: InnerFilters[string]) =>
    ((value as SelectOption<string> | null | undefined)?.value ?? null) as string | null;

const buildFilter = ({
    tin,
    from,
    to,
    typeIds,
    violationStatus,
    notificationStatus,
}: Omit<ViolationsRequestParams, 'sortings' | 'page' | 'size'>): ViolationFilterNode => {
    const nodes: ViolationFilterNode[] = [
        {type: 'ISNULL', field: 'validTo', values: []},
        {type: 'EQ', field: 'payerTin', values: [tin]},
    ];

    const fromPeriod = from ? format(from, 'yyyyMM') : null;
    const toPeriod = to ? format(to, 'yyyyMM') : null;

    if (fromPeriod && toPeriod) {
        nodes.push(
            fromPeriod === toPeriod
                ? {type: 'EQ', field: 'taxPeriod', values: [fromPeriod]}
                : {type: 'BTW', field: 'taxPeriod', values: [fromPeriod, toPeriod]},
        );
    }

    if (typeIds.length) {
        nodes.push({type: 'IN', field: 'type', values: typeIds});
    }

    if (violationStatus) {
        nodes.push({type: 'EQ', field: 'status.code', values: [violationStatus]});
    }

    if (notificationStatus) {
        nodes.push({type: 'EQ', field: 'notificationStatus.code', values: [notificationStatus]});
    }

    return {type: 'AND', children: nodes};
};

const mapSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings)[0] ?? [];
    if (!field || value == null) return null;
    return [{field, order: value ? 'DESC' : 'ASC'} as const];
};

sample({
    clock: ViolationsGate.open,
    target: [fetchViolationTypesFx, fetchViolationStatusesFx, fetchNotificationStatusesFx],
});

sample({
    clock: merge([ViolationsGate.open, tableStateChanged, dateModel.changed]),
    source: combine({
        gate: ViolationsGate.state,
        date: dateModel.state,
        filters: filters$,
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    filter: ViolationsGate.status,
    fn: ({gate, date, filters, sortings, pageNumber, pageSize}) => ({
        filter: buildFilter({
            tin: gate.tin,
            from: date.range.from,
            to: date.range.to,
            typeIds: getSelectedIds(filters.source),
            violationStatus: getSelectedCode(filters.violationStatus),
            notificationStatus: getSelectedCode(filters.sendingStatus),
        }),
        sorts: mapSorts(sortings),
        page: {
            page: pageNumber,
            size: pageSize,
        },
    }),
    target: fetchViolationsFx,
});

const $violationsState = combine({
    data: data$,
    filters: filters$,
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    violationTypes: violationTypes$,
    violationStatuses: violationStatuses$,
    notificationStatuses: notificationStatuses$,
    pending: combine(
        [
            fetchViolationsFx.pending,
            fetchViolationTypesFx.pending,
            fetchViolationStatusesFx.pending,
            fetchNotificationStatusesFx.pending,
        ],
        (list) => list.some(Boolean),
    ),
});

export const violationsModel = {
    state: $violationsState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    pageNumberChanged,
    pageSizeChanged,
};
